import { useState } from 'react';

import { PersonaType, Recommender, RecommenderDocument } from '@youscience/commonapp-service-common';

import { CoreBox, CoreTooltip, CoreTypography } from '@youscience/core';

import useCommonAppStore from '@store/CommonAppStore';

import EditIcon from '@images/pencil-button.svg?react';
import DeleteIcon from '@images/bucket-button.svg?react';

import { formatDateWithFallback } from '@utils/formatter';

import ActionButton from '@components/ActionButton';
import { ConfirmationMessage } from '@components/ConfirmationMessage';
import LabelWithDate from '@components/LabelWithDate';
import FormStatus from '@components/FormStatus';

import { StyledDivider } from '@components/common.styles';
import { StyledJustifiedBox, StyledTeacher, StyledTeacherButtonGroup } from './TeacherCard.styles';
import { StyledTooltipBox } from '../../PlannerSchoolCard/components/PlannerSchoolCardRecommenders/PlannerSchoolCardRecommenders.styles';

import InfoIcon from '@images/info.svg?react';

type RecommenderWithCommonApp = Recommender & {
  isFromCommonApp?: boolean;
};

type TeacherCardProps = {
  index: number;
  teacher: RecommenderWithCommonApp;
  memberId: number;
  applicantTeachers: RecommenderDocument[];
  isDisabled?: boolean;
  handleRemove: (recommenderId: number) => Promise<void>;
  handleEdit: (recommenderId: number) => Promise<void>;
};

export const TeacherCard = ({
  index,
  teacher,
  memberId,
  applicantTeachers,
  isDisabled,
  handleRemove,
  handleEdit,
}: TeacherCardProps) => {
  const [isOpenConfirmationMessage, setIsOpenConfirmationMessage] = useState(false);

  const { applicant, formStatuses } = useCommonAppStore((state) => state);

  const handleRemoveClick = async () => {
    await handleRemove(teacher.recommenderId);
  };

  const handleEditClick = async () => {
    await handleEdit(teacher.recommenderId);
  };

  const applicantTeacher = applicantTeachers?.find((t) => t.recommenderId === teacher.recommenderId);
  const subject = applicantTeacher?.subject;
  const firstName = applicantTeacher?.firstName ?? teacher.firstName;
  const lastName = applicantTeacher?.lastName ?? teacher.lastName;

  const isTeacherAssignedToAnotherMember = applicant?.members
    ?.filter((member) => member.memberId !== memberId)
    .some((member) =>
      member.recommenders.some(
        (recommender) =>
          recommender.recommenderId === teacher.recommenderId && recommender.personaType === PersonaType.TEACHER,
      ),
    );

  const formStatus = formStatuses?.members
    ?.find((member) => member.memberId === memberId)
    ?.teachers?.find((record) => record.recommenderId === teacher.recommenderId);

  const isFromCommonApp = teacher.isFromCommonApp ?? false;
  const isEditRemoveDisabled = isDisabled || isFromCommonApp;

  return (
    <>
      <StyledJustifiedBox key={`Teacher${teacher.recommenderId}`}>
        <StyledTeacher>
          <CoreBox>
            <CoreTypography>{`${index + 1}.`}</CoreTypography>
          </CoreBox>
          <CoreBox>
            <CoreBox>
              <StyledTooltipBox>
                <CoreTypography>
                  {subject ? `${firstName} ${lastName}, ${subject}` : `${firstName} ${lastName}`}
                </CoreTypography>
                {isFromCommonApp ? (
                  <CoreTooltip title="This recommender was added on Common App's website">
                    <CoreTypography>
                      <InfoIcon />
                    </CoreTypography>
                  </CoreTooltip>
                ) : null}
              </StyledTooltipBox>
            </CoreBox>
            <CoreBox sx={{ display: 'flex', gap: '.5rem' }}>
              <LabelWithDate label="Assigned" date={formatDateWithFallback(formStatus?.assignedDate as string)} />
              <StyledDivider />
              <FormStatus formStatus={formStatus} isTeacher></FormStatus>
            </CoreBox>
          </CoreBox>
        </StyledTeacher>
        {formStatus?.status !== 'Submitted' ? (
          <StyledTeacherButtonGroup>
            <CoreTooltip title={isFromCommonApp ? "This recommender was added on Common App's website" : ''}>
              <CoreBox>
                <ActionButton
                  type="button"
                  variant="outlined"
                  onClick={() => setIsOpenConfirmationMessage(true)}
                  busyText="Removing"
                  startIcon={<DeleteIcon />}
                  disabled={isEditRemoveDisabled}
                >
                  Remove
                </ActionButton>
              </CoreBox>
            </CoreTooltip>

            <CoreTooltip title={isFromCommonApp ? "This recommender was added on Common App's website" : ''}>
              <CoreBox>
                <ActionButton
                  type="button"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEditClick}
                  disabled={isEditRemoveDisabled}
                >
                  Edit
                </ActionButton>
              </CoreBox>
            </CoreTooltip>
          </StyledTeacherButtonGroup>
        ) : null}
      </StyledJustifiedBox>

      {isOpenConfirmationMessage ? (
        <ConfirmationMessage
          icon={<DeleteIcon />}
          title="Remove teacher?"
          subtitle={
            isTeacherAssignedToAnotherMember
              ? `Are you sure you want to remove ${firstName} ${lastName} as a recommender for this college?`
              : `Are you sure you want to remove ${firstName} ${lastName}'s assignment?`
          }
          goNextButtonTitle="Continue"
          goNextActionButtonTitle="Removing"
          isOpenConfirmationMessage={isOpenConfirmationMessage}
          setIsOpenConfirmationMessage={setIsOpenConfirmationMessage}
          onGoNextBtnClick={handleRemoveClick}
        />
      ) : null}
    </>
  );
};
